'use client';

import { useState, useMemo, useRef, useCallback } from 'react';
import Image from '@src/components/atoms/safe-image';
import { useRouter, useParams } from 'next/navigation';

// Components
import ModalSuccess from '@src/components/century-21-or-company/modal-success';
import PropertyTypeDesktop from '@src/components/molecules/homepage/property-type-dekstop';
import Search from '@src/components/molecules/homepage/search';
import ModalSearchDekstop from '@src/components/molecules/homepage/modal-search-dekstop/modal-search-dekstop';
import ModalSearchMobile from '@src/components/molecules/homepage/modal-search-mobile/modal-search-mobile';
import SkeletonLoader from '@src/components/atoms/skeleton-loader';

import useWindowsSize from '@src/hooks/useWindowSize';
import useOutsideAlerter from '@src/hooks/useOutsideAlerter';

// Api
import { useGetListLocation } from '@src/apis/shared/query/use-get-list-location';
import { useGetSearchAgents } from '@src/apis/shared/query/use-get-search-agents';
import { useGetSearchOffices } from '@src/apis/shared/query/use-get-search-offices';

// Hooks
import { useDebounce } from '@src/hooks/useDebounce';
import stringToDash from '@src/helper/stringToDash';

// Assets
const Century21AwsS3Storage = `${process.env.NEXT_PUBLIC_AWS_S3_URL}/production.storages/storages/store/century-21-indonesia/c3e34d8d209a5d92d42d1c7d7bdf5812b5b93e3b.jpg`;

const highlightText = (text, highlight) => {
  if (!highlight) return text;
  if (!text) return '';

  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));

  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            part.toLowerCase() === highlight.toLowerCase()
              ? { fontFamily: 'Satoshi Bold' }
              : {}
          }
        >
          {part}
        </span>
      ))}
    </span>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const router = useRouter();
  const { dd22XL } = useWindowsSize();
  const { companySlug } = useParams()

  const [isOpenModalSuccess, setIsOpenModalSuccess] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Search
  const [searchType, setSearchType] = useState('jual'); // jual | sewa | agent | consignment | kantor
  const [displaySearch, setDisplaySearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [params, setParams] = useState({
    _limit: 10,
    q: '',
  });

  const handleSearchTypeChange = (type) => {
    if (searchType !== type) {
      setSearchType(type);
      setDisplaySearch('');
      setParams({ _limit: 10, q: '' });
      setShowSearch(false);
    }
  };

  const searchPlaceholderText = useMemo(() => {
    switch (searchType) {
      case 'jual':
      case 'sewa':
        return 'Lokasi, area, keyword';
      case 'agent':
        return 'Nama, nomor telepon, no. registrasi';
      case 'kantor':
        return 'Nama kantor, kota, area';
      default:
        return 'Search...';
    }
  }, [searchType]);

  // Use different APIs based on search type
  const { data: locationData, isFetching: isFetchingLocation, fetchNextPage: fetchNextPageLocation, hasNextPage: hasNextPageLocation, isFetchingNextPage: isFetchingNextPageLocation } =
    useGetListLocation({ 
      params,
      options: { enabled: searchType === 'jual' || searchType === 'sewa' }
    });

  const { data: agentData, isFetching: isFetchingAgent, fetchNextPage: fetchNextPageAgent, hasNextPage: hasNextPageAgent, isFetchingNextPage: isFetchingNextPageAgent } =
    useGetSearchAgents({ 
      params,
      options: { enabled: searchType === 'agent' }
    });

  const { data: officeData, isFetching: isFetchingOffice, fetchNextPage: fetchNextPageOffice, hasNextPage: hasNextPageOffice, isFetchingNextPage: isFetchingNextPageOffice } =
    useGetSearchOffices({ 
      params,
      options: { enabled: searchType === 'kantor' }
    });

  // Combine data and loading states based on search type using useMemo
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useMemo(() => {
    if (searchType === 'agent') {
      return {
        data: agentData,
        isFetching: isFetchingAgent,
        fetchNextPage: fetchNextPageAgent,
        hasNextPage: hasNextPageAgent,
        isFetchingNextPage: isFetchingNextPageAgent
      };
    } else if (searchType === 'kantor') {
      return {
        data: officeData,
        isFetching: isFetchingOffice,
        fetchNextPage: fetchNextPageOffice,
        hasNextPage: hasNextPageOffice,
        isFetchingNextPage: isFetchingNextPageOffice
      };
    } else {
      return {
        data: locationData,
        isFetching: isFetchingLocation,
        fetchNextPage: fetchNextPageLocation,
        hasNextPage: hasNextPageLocation,
        isFetchingNextPage: isFetchingNextPageLocation
      };
    }
  }, [
    searchType,
    agentData, isFetchingAgent, fetchNextPageAgent, hasNextPageAgent, isFetchingNextPageAgent,
    officeData, isFetchingOffice, fetchNextPageOffice, hasNextPageOffice, isFetchingNextPageOffice,
    locationData, isFetchingLocation, fetchNextPageLocation, hasNextPageLocation, isFetchingNextPageLocation
  ]);

  const listLocation = useMemo(() => {
    if (!data?.pages?.length) return [];

    const flatData = data.pages.map((d) => d?.data?.data ?? []).flat();
    
    // Transform agent and office data to match the expected format
    if (searchType === 'agent') {
      return flatData.map(agent => ({
        id: agent.id,
        name: agent.name || agent.full_name || '',
        slug: 'Agent',
        ...agent
      }));
    } else if (searchType === 'kantor') {
      return flatData.map(office => ({
        id: office.id,
        name: office.name || office.office_name || '',
        slug: 'Kantor',
        ...office
      }));
    }
    
    return flatData;
  }, [data, searchType]);

  const { debounceFn } = useDebounce({
    onChange: (newParams) => setParams({ ...params, ...newParams }),
  });

  const handleChangeSearch = (e) => {
    const newSearch = e.target.value ?? '';

    setDisplaySearch(newSearch);
    debounceFn({ q: newSearch.trim() });
  };

  const resetSearch = () => {
    setParams({ _limit: 10, q: '' });
    setDisplaySearch('');
  };

  const handleCloseModalSuccess = () => {
    setIsOpenModalSuccess(false);
  };

  const handleSelectLocation = (item) => {
    let url = `/office/${companySlug}/list-listing?property_type_id=${searchType === 'jual' ? 1 : 2}`

    if(searchType === 'jual' || searchType === 'sewa') {
      if (item.slug === 'Area') {
        url += `&area_ids=${item.id}`
      } else if (item.slug === 'City') {
        url += `&city_id=${item.id}`
      } else {
        url += `&q=${item.name}`
      }

    } else if (searchType === 'agent') {
      url = `/marketing/${item.username}`
    } else if (searchType === 'kantor') {
      url = `/office/${companySlug}/office/${item.slug}`
    }

    router.push(url)
  };

  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  useOutsideAlerter(ref, () => setShowSearch(false));

  return (
    <>
      <div
        className="relative flex items-center h-[490px]
        dd3-xl:h-[412px]
        dd3-2xl:h-[474px] 
        dd3-xl:h-[474px] 
        dd3-lg:h-[442px]"
      >
        <div className="absolute top-0 left-0 z-[1] w-full h-full">
          <Image
            src={Century21AwsS3Storage}
            alt="Hero image left"
            width={1000}
            height={1000}
            className="w-full h-full object-cover brightness-[80%]"
            onLoad={() => setLoaded(true)}
          />
        </div>

        <div
          className="w-full max-w-[1376px] relative h-full z-[2] mx-auto py-[80px]
          dd3-2xl:py-[64px] dd3-2xl:max-w-[unset] dd2-2xl:px-[64px]
          dd3-xl:py-[48px] dd3-xl:px-[40px]
          dd3-lg:py-[40px] dd3-lg:px-[32px]
          dd3-md:px-[24px]"
        >
          <h1
            className="font-satoshi-bold max-w-[909px] text-[#FAFAFA] text-[64px] leading-[76px] tracking-[-2.56px] mb-4 
            dd3-2xl:text-[52px] dd3-2xl:leading-[62px] dd3-2xl:tracking-[-2.08px]
            dd3-md:text-[33px] dd3-md:leading-[39.6px] dd3-md:tracking-[-0.99px]"
          >
            Bukan sekedar ruang, <br /> properti adalah awal cerita baru
          </h1>
          <p
            className="font-satoshi mb-[32px] max-w-[464px] text-[#FAFAFA] text-[17px]/[23.8px] tracking-[-0.17px] mb-[40px]"
          >
            29 tahun kami menjadi bagian dari cerita banyak orang dalam
            menemukan properti impian.
          </p>

          {/* Search */}
          <div ref={!dd22XL ? ref : null}>
            <PropertyTypeDesktop
              propertyType={searchType}
              setPropertyType={handleSearchTypeChange}
            />

            <div className="relative max-w-[559px]">
              <Search
                displaySearch={displaySearch}
                handleChangeSearch={handleChangeSearch}
                setShowSearch={setShowSearch}
                resetSearch={resetSearch}
                placeholder={searchPlaceholderText}
              />

              <ModalSearchDekstop
                showSearch={showSearch}
                displaySearch={displaySearch}
                listLocation={listLocation}
                lastItemRef={lastItemRef}
                isFetching={isFetching}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                handleSelectLocation={handleSelectLocation}
                highlightText={highlightText}
                searchType={searchType}
              />
              {showSearch && (
                <ModalSearchMobile
                  isOpen={showSearch}
                  onClose={() => setShowSearch(false)}
                  displaySearch={displaySearch}
                  handleChangeSearch={handleChangeSearch}
                  listLocation={listLocation}
                  lastItemRef={lastItemRef}
                  isFetching={isFetching || isFetchingNextPage}
                  handleSelectLocation={handleSelectLocation}
                  highlightText={highlightText}
                  searchType={searchType}
                />
              )}
            </div>
          </div>
        </div>

        <SkeletonLoader
          isShow={!loaded}
          className="z-[3] w-full h-full object-cover"
        />
      </div>
      <ModalSuccess
        isOpen={isOpenModalSuccess}
        title="Terima Kasih!"
        description="Anda akan segera dihubungi oleh Marketing yang bersangkutan. Pastikan nomor WhatsApp yang telah dimasukkan aktif."
        onClose={handleCloseModalSuccess}
      />
    </>
  );
};

export default Hero;
